import React from 'react';
import { 
    Box, 
    Typography, 
    Card, 
    CardContent, 
    Chip, 
    Divider,
    Button,
    IconButton,
    Paper,
    Grid,
    CircularProgress // Import CircularProgress for better loading state
} from '@mui/material';
import { 
    Download, 
    Visibility, 
    ArrowBack,
    Email,
    Phone,
    Person,
    CalendarToday,
    AttachFile,
    School // Added for programme selector
} from '@mui/icons-material';
import { 
    APIAppUnderGraduateListById,
    APIAppGraduateListById,
    APIAppShortCourseListById 
} from '../../utilities/APIs';
// Import the ProgrammeAPI to fetch programme data
import { ProgrammeAPI } from '../../utilities/ProgrammeAPI';

export class RespondentAnswersView extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            applicationData: null,
            formConfig: null,
            programmeLookup: new Map() // State to hold our ID -> name mapping
        };
    }

    async componentDidMount() {
        await this.loadInitialData();
    }

    // New function to handle loading all necessary data in parallel
    async loadInitialData() {
        try {
            this.setState({ loading: true, error: null });

            // Fetch both application data and all programmes at the same time
            const [applicationResult, programmesResult] = await Promise.all([
                this.fetchApplicationData(),
                ProgrammeAPI.getActiveStructuredProgrammes()
            ]);

            // Process application data
            const applicationData = applicationResult;
            let formConfig = null;
            if (applicationData.formConfig) {
                try {
                    formConfig = JSON.parse(applicationData.formConfig);
                } catch (e) {
                    console.warn('Failed to parse form config:', e);
                }
            }

            // Process and flatten programme data for easy lookup
            const programmeLookup = this.flattenProgrammes(programmesResult.data);

            this.setState({
                applicationData,
                formConfig,
                programmeLookup,
                loading: false
            });

        } catch (error) {
            console.error('Error loading initial data:', error);
            this.setState({
                error: error.message || 'Failed to load application details',
                loading: false
            });
        }
    }

    // Helper function to flatten the nested programme structure into a simple Map
    flattenProgrammes(structuredData) {
        const lookup = new Map();
        if (!structuredData) return lookup;

        // Iterate through faculties -> departments -> levels -> programmes
        for (const facultyKey in structuredData) {
            const facultyObj = structuredData[facultyKey];
            for (const departmentKey in facultyObj) {
                const departmentObj = facultyObj[departmentKey];
                for (const levelKey in departmentObj) {
                    const levelArray = departmentObj[levelKey];
                    if (Array.isArray(levelArray)) {
                        levelArray.forEach(programme => {
                            // Use programme.id as the key and the whole programme object as the value
                            lookup.set(programme.id, programme);
                        });
                    }
                }
            }
        }
        return lookup;
    }

    // This function now only fetches application-specific data
    async fetchApplicationData() {
        const { applicationId, applicationType } = this.props;
        
        let apiUrl;
        if (applicationType.includes('UNDER')) {
            apiUrl = APIAppUnderGraduateListById + applicationId;
        } else if (applicationType.includes('GRADUATE')) {
            apiUrl = APIAppGraduateListById + applicationId;
        } else if (applicationType.includes('SHORT')) {
            apiUrl = APIAppShortCourseListById + applicationId;
        } else {
            throw new Error('Unknown application type: ' + applicationType);
        }

        const token = localStorage.getItem('jwtToken');
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.status === 'success') {
            return result.data;
        } else {
            throw new Error(result.message || 'Failed to load application data');
        }
    }


    formatValue(field, value) {
        if (value === null || value === undefined || value === '') return 'Not provided';

        switch (field.type) {
            // ### START: NEW CASE FOR programme-selector ###
            case 'programme-selector':
                const { programmeLookup } = this.state;
                // Value is an array of programme IDs
                const selectedIds = Array.isArray(value) ? value : [value];

                if (selectedIds.length === 0) return 'No programmes selected.';

                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedIds.map((progId) => {
                            const programme = programmeLookup.get(progId);
                            const label = programme ? programme.name : `Unknown Programme (ID: ${progId})`;
                            return (
                                <Chip
                                    key={progId}
                                    icon={<School />}
                                    label={label}
                                    variant="outlined"
                                    color="primary"
                                />
                            );
                        })}
                    </Box>
                );
            // ### END: NEW CASE FOR programme-selector ###

            case 'email':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" color="primary" />
                        <Typography variant="body2">{value}</Typography>
                    </Box>
                );
            case 'file':
                if (Array.isArray(value)) {
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {value.map((fileName, index) => (
                                <Chip
                                    key={index}
                                    icon={<AttachFile />}
                                    label={fileName}
                                    variant="outlined"
                                    onClick={() => this.downloadFile(fileName)}
                                    clickable
                                />
                            ))}
                        </Box>
                    );
                } else {
                    return (
                        <Chip
                            icon={<AttachFile />}
                            label={value}
                            variant="outlined"
                            onClick={() => this.downloadFile(value)}
                            clickable
                        />
                    );
                }
            case 'checkbox':
                return value ? 'Yes' : 'No';
            case 'select':
            case 'radio':
                return (
                    <Chip 
                        label={value} 
                        variant="outlined" 
                        size="small"
                        color="primary"
                    />
                );
            default:
                return Array.isArray(value) ? value.join(', ') : String(value);
        }
    }

    downloadFile(fileName) {
        // Implement file download logic
        const { applicationData } = this.state;
        const applicationType = applicationData.formType || 'default';
        const downloadUrl = `/api/apply/student/${applicationType}/serve/profile/photo/${fileName}`;
        window.open(downloadUrl, '_blank');
    }

    getFieldIcon(fieldType) {
        switch (fieldType) {
            case 'programme-selector':
                return <School fontSize="small" />;
            case 'email':
                return <Email fontSize="small" />;
            case 'text':
                if (fieldType.toLowerCase().includes('phone')) {
                    return <Phone fontSize="small" />;
                }
                return <Person fontSize="small" />;
            case 'file':
                return <AttachFile fontSize="small" />;
            case 'date':
                return <CalendarToday fontSize="small" />;
            default:
                return null;
        }
    }

    render() {
        const { onBack } = this.props;
        const { loading, error, applicationData, formConfig } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2 }}>
                    <CircularProgress />
                    <Typography>Loading Application Details...</Typography>
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
                    <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
                    <Button variant="outlined" onClick={onBack}>
                        Go Back
                    </Button>
                </Box>
            );
        }        if (!applicationData) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
                    <Typography sx={{ mb: 2 }}>No application data found</Typography>
                    <Button variant="outlined" onClick={onBack}>
                        Go Back
                    </Button>
                </Box>
            );
        }
        
        const submissionData = applicationData.formSubmissionJson ? JSON.parse(applicationData.formSubmissionJson) : {};

        return (
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton onClick={onBack} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4" component="h1">
                        Application Details
                    </Typography>
                </Box>

                {/* Application Info Card */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                    {applicationData.formName || 'Application Form'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Application ID: {applicationData.id}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>                                <Typography variant="body2" color="text.secondary">
                                    Submitted by: {applicationData.submittedBy}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Submitted on: {new Date(applicationData.createdAt).toLocaleDateString()}
                                </Typography>
                                {applicationData.formType && (
                                    <Typography variant="body2" color="text.secondary">
                                        Form Type: {applicationData.formType}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>                {/* Sections and Fields */}
                {formConfig && formConfig.sections ? (
                    formConfig.sections.map((section, sectionIndex) => (
                        <Card key={section.id || sectionIndex} sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                    {section.name}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                <Grid container spacing={3}>
                                    {section.fields && section.fields.map((field, fieldIndex) => {
                                        const value = submissionData[field.id];
                                        
                                        // Don't render if there's no value and the field is not required
                                        // This can clean up the view from empty optional fields
                                        if (!value && !field.required) {
                                            return null;
                                        }

                                        return (
                                            <Grid item xs={12} md={6} key={field.id || fieldIndex}>
                                                <Paper 
                                                    variant="outlined" 
                                                    sx={{ p: 2, height: '100%' }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                                        {this.getFieldIcon(field.type)}
                                                        <Typography 
                                                            variant="subtitle2" 
                                                            sx={{ fontWeight: 'medium' }}
                                                        >
                                                            {field.label}
                                                            {field.required && (
                                                                <span style={{ color: 'red' }}> *</span>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ ml: this.getFieldIcon(field.type) ? 3 : 0 }}>
                                                        {this.formatValue(field, value)}
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </CardContent>
                        </Card>
                    ))
                ) : (
                    /* Fallback when formConfig is not available */
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                Application Data
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Grid container spacing={3}>
                                {Object.entries(submissionData).map(([fieldId, value]) => (
                                    <Grid item xs={12} md={6} key={fieldId}>
                                        <Paper 
                                            variant="outlined" 
                                            sx={{ p: 2, height: '100%' }}
                                        >
                                            <Typography 
                                                variant="subtitle2" 
                                                sx={{ fontWeight: 'medium', mb: 1 }}
                                            >
                                                {fieldId}
                                            </Typography>
                                            
                                            <Typography variant="body2">
                                                {Array.isArray(value) ? value.join(', ') : String(value || 'Not provided')}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button 
                        variant="outlined" 
                        startIcon={<Download />}
                        onClick={() => {
                            // Implement export functionality
                            const dataStr = JSON.stringify(applicationData, null, 2);
                            const dataBlob = new Blob([dataStr], {type: 'application/json'});
                            const url = URL.createObjectURL(dataBlob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `application_${applicationData.id}.json`;
                            link.click();
                        }}
                    >
                        Export Data
                    </Button>
                    <Button variant="contained" onClick={onBack}>
                        Back to List
                    </Button>
                </Box>
            </Box>
        );
    }
}

export default RespondentAnswersView;